import { z } from 'zod';

// =============================================================================
// MOBILE APP VERSION POLICY — GET /api/mobile/version-policy?platform=ios|android
//
// Public (no sign-in): the mobile app asks on launch, whether or not anyone is signed in,
// and again on each return to the foreground. The answer is the platform's minimum and
// recommended versions, which live in `platform_config` (PlatformDO) and change through
// `PATCH /api/admin/config` — never a constant in the app or the route.
//
// WIRE COMPATIBILITY — THIS CONTRACT ONLY GROWS ADDITIVELY. Every app build ever shipped
// keeps calling this endpoint with the request it was built with, and parses the answer
// with the schema compiled into it, for as long as it stays installed. So:
//   - the request may only ACCEPT more: a new `platform` value, a new OPTIONAL parameter;
//   - the response may only GAIN fields: never rename, remove or retype a field, never
//     change a version's format (an installed app that cannot parse the answer treats the
//     check as failed, so it would never block again), and never change a field's meaning.
// `mobile-version-policy.test.ts` pins the fields every shipped build depends on.
// =============================================================================

/** One version component: a whole number without leading zeros. */
const VERSION_PART = '(0|[1-9][0-9]{0,5})';

/**
 * A store version as the policy states it: exactly MAJOR.MINOR.PATCH (`1.0.0`), the shape of
 * `expo.version` in `apps/mobile/app.json`, which both stores' builds are versioned from.
 */
export const MOBILE_APP_VERSION_RE = new RegExp(`^${VERSION_PART}\\.${VERSION_PART}\\.${VERSION_PART}$`);

export const mobileAppVersionSchema = z
  .string()
  .regex(MOBILE_APP_VERSION_RE, 'must be a MAJOR.MINOR.PATCH version, e.g. 1.0.0');

export const MOBILE_PLATFORMS = ['ios', 'android'] as const;
export const mobilePlatformSchema = z.enum(MOBILE_PLATFORMS);
export type MobilePlatform = z.infer<typeof mobilePlatformSchema>;

export const mobileVersionPolicyQuery = z.object({
  platform: mobilePlatformSchema,
});

export const mobileVersionPolicySchema = z.object({
  /** An install below this version is blocked, and sent to the store to update. */
  minimum_version: mobileAppVersionSchema,
  /** An install below this version is asked to update, and may dismiss the prompt. */
  recommended_version: mobileAppVersionSchema,
});
export type MobileVersionPolicy = z.infer<typeof mobileVersionPolicySchema>;

export const mobileVersionPolicyResponse = z.object({
  data: mobileVersionPolicySchema,
});
export type MobileVersionPolicyResponse = z.infer<typeof mobileVersionPolicyResponse>;

/**
 * The `platform_config` column that stores each platform's policy field. These are also the
 * field names `PATCH /api/admin/config` takes (`updatePlatformConfigBody`).
 */
export const MOBILE_VERSION_POLICY_COLUMNS = {
  ios: {
    minimum_version: 'mobile_ios_minimum_version',
    recommended_version: 'mobile_ios_recommended_version',
  },
  android: {
    minimum_version: 'mobile_android_minimum_version',
    recommended_version: 'mobile_android_recommended_version',
  },
} as const satisfies Record<MobilePlatform, Record<keyof MobileVersionPolicy, string>>;

/**
 * Read a version for comparison: one to three dot-separated whole numbers, a missing part
 * read as 0 (`1.0` is `1.0.0`). Lenient on purpose — it also reads the version an installed
 * app reports about itself. Anything else is null.
 */
export function parseMobileAppVersion(version: string): [number, number, number] | null {
  const match = /^(\d{1,6})(?:\.(\d{1,6}))?(?:\.(\d{1,6}))?$/.exec(version.trim());
  if (!match) return null;
  return [Number(match[1]), Number(match[2] ?? 0), Number(match[3] ?? 0)];
}

/**
 * True when `installed` is strictly below `required`. False when either cannot be read: a
 * version this cannot compare is never judged below a policy, so it never blocks an install.
 */
export function isMobileAppVersionBelow(installed: string, required: string): boolean {
  const have = parseMobileAppVersion(installed);
  const need = parseMobileAppVersion(required);
  if (!have || !need) return false;
  for (let i = 0; i < 3; i++) {
    if (have[i] !== need[i]) return have[i] < need[i];
  }
  return false;
}
